<script>
  import { createEventDispatcher, tick } from "svelte";
  import { tooltip } from "svelte-utilities";
  import { DimText, LinearDisplayFormat } from "dimtext";

  import PlusIcon from "@local/assets/icons/plus.svg";
  import MinusIcon from "@local/assets/icons/minus.svg";
  import CheckIcon from "@local/assets/icons/check.svg";
  import XIcon from "@local/assets/icons/x.svg";

  export const activate = () => {
    handleActivate();
  };

  export let settings;

  const dispatch = createEventDispatcher();
  const dt = new DimText({ defaultUnit: settings.displayUnit });

  let input;
  let inputContents = "0";
  let inputValue = dt.parse("0").value;
  let operations = [];
  let initialValue = null;

  $: unit = settings.displayUnit;
  $: updateContents(inputValue);
  $: result = getResult(initialValue, operations);

  function formatter(value) {
    const v = value?.format(LinearDisplayFormat[settings.dimFormat], settings.dimPrecision, {
      displayUnit: settings.displayUnit,
      showUnit: true,
      unitSymbols: "ascii",
    });
    if (v) return v;
    return null;
  }

  function setFractionalPrecision(p, value) {
    if ([2, 4, 8, 16, 32, 64].includes(value.den)) {
      const precision = {
        2: 1,
        4: 2,
        8: 3,
        16: 4,
        32: 5,
        64: 6,
      }[value.den];
      p.is_standard = true;
      p.value = precision;
      p.type = "fractional";
    }
  }

  function setSigFigPrecision(p, value) {
    const precision = value.sig.toString().length;
    p.is_standard = true;
    p.value = precision;
    p.type = "decimal";
  }

  function quantPrecision(q) {
    const unit = {
      inches: "inches",
      feet: "inches",
      millimeters: "millimeters",
      centimeters: "millimeters",
      meters: "millimeters",
    }[q.unit];

    if (!unit) {
      return { is_standard: false, unit: undefined, value: undefined };
    }

    const p = { is_standard: false, unit, value: undefined };

    if (typeof q.value === "number") {
      const decPlaces = q.value.toString().split(".")[1]?.length || 0;
      p.value = decPlaces;
      p.is_standard = true;
    } else if ("ipart" in q.value) {
      const fpart = q.value.fpart;
      if ("den" in fpart) {
        setFractionalPrecision(p, fpart);
      } else if ("sig" in fpart) {
        setSigFigPrecision(p, fpart);
      }
    } else if ("num" in q.value) {
      setFractionalPrecision(p, q.value);
    } else if ("sig" in q.value) {
      setSigFigPrecision(p, q.value);
    }

    return p;
  }

  function dimPrecision(dim) {
    let p;

    dim.quants.forEach((q) => {
      const qp = quantPrecision(q);
      if (!p) {
        p = qp;
      } else if (p.is_standard && qp.is_standard && qp.unit === p.unit) {
        p.value = Math.max(p.value, qp.value);
      } else {
        p.is_standard = false;
      }
    });

    return p;
  }

  function precisionsAreCompatible(precisions) {
    if (precisions.some((p) => !p?.is_standard)) return false;

    let type;
    for (const p of precisions) {
      if (p.type && !type) {
        type = p.type;
      } else if (p.type && p.type !== type) {
        return false;
      }
    }

    const [first, ...rest] = precisions;
    if (rest.some((p) => p.unit !== first.unit)) return false;
    return true;
  }

  function displayFormat(precisions) {
    const first = precisions.find((p) => p.type);
    if (!first) return LinearDisplayFormat[settings.dimFormat];
    if (first.type === "decimal") return LinearDisplayFormat.DECIMAL;
    if (first.type === "fractional") return LinearDisplayFormat.FRACTIONAL;
    return LinearDisplayFormat[settings.dimFormat];
  }

  function getResult(initialValue, operations) {
    if (!initialValue) return null;
    if (operations.some((op) => !op.term)) {
      return null;
    }

    const iv = initialValue.toNumber(unit);
    const val = operations.reduce((acc, op) => {
      if (op.operand === "add") {
        return acc + op.term.toNumber(unit);
      } else if (op.operand === "subtract") {
        return acc - op.term.toNumber(unit);
      }

      return acc;
    }, iv);

    if (!Number.isFinite(val)) {
      return null;
    }

    const result = dt.parse(val.toString()).value;

    const ivp = dimPrecision(initialValue);
    const opps = operations.map((op) => dimPrecision(op.term));
    const precisions = [ivp, ...opps];
    if (precisionsAreCompatible(precisions)) {
      const maxPrecision = Math.max(...precisions.map((p) => p.value));
      const format = displayFormat(precisions);
      const formatted = result.format(format, maxPrecision, {
        displayUnit: ivp.unit,
        showUnit: true,
        unitSymbols: "ascii",
      });
      const parser = new DimText({ defaultUnit: ivp.unit });
      const reparsed = parser.parse(formatted);
      if (reparsed.ok) {
        return reparsed.value;
      }
    }

    return result;
  }

  function updateContents(value) {
    if (value && value.toString() !== inputContents) {
      inputContents = value.toString();
    }
  }

  function handleActivate() {
    if (input) {
      clear();
    }
  }

  function parseInput(val) {
    try {
      let result = dt.parse(val);
      if (!result.ok) throw result.err;
      inputValue = result.value;
    } catch (err) {
      inputValue = dt.parse("0").value;
    }
  }

  function focus() {
    if (input) {
      input.focus();
      input.select();
    }
  }

  async function handleKeydown(evt) {
    if (evt.key === "Enter") {
      evt.stopPropagation();
      parseInput(evt.target.value);
      await tick();

      if (inputValue?.toNumber(unit) !== 0) {
        if (initialValue) {
          const lastOperation = operations[operations.length - 1];

          if (!lastOperation) {
            if (inputValue.sign === -1) {
              inputValue.sign = 1;

              operations.push({
                operand: "subtract",
                term: inputValue,
              });
            } else {
              operations.push({
                operand: "add",
                term: inputValue,
              });
            }
          } else if (!lastOperation.term) {
            lastOperation.term = inputValue;
          } else {
            if (inputValue.sign === -1) {
              inputValue.sign = 1;

              operations.push({
                operand: "subtract",
                term: inputValue,
              });
            } else {
              operations.push({
                operand: "add",
                term: inputValue,
              });
            }
          }

          operations = operations;
          inputValue = dt.parse("0").value;
        } else {
          initialValue = inputValue;
          inputValue = dt.parse("0").value;
        }
      }

      await tick();
      focus();
    }
  }

  async function handleAdd() {
    parseInput(inputContents);
    await tick();

    if (!initialValue && inputValue?.toNumber(unit) !== 0) {
      initialValue = inputValue;
      inputValue = dt.parse("0").value;
      operations.push({
        operand: "add",
        term: null,
      });
    } else if (initialValue && inputValue?.toNumber(unit) === 0) {
      const lastOperation = operations[operations.length - 1];
      if (lastOperation && !lastOperation.term) {
        lastOperation.operand = "add";
      } else {
        operations.push({
          operand: "add",
          term: null,
        });
      }
    } else if (initialValue && inputValue?.toNumber(unit) !== 0) {
      const lastOperation = operations[operations.length - 1];
      if (lastOperation && !lastOperation.term) {
        lastOperation.operand = "add";
        lastOperation.term = inputValue;
      } else {
        operations.push({
          operand: "add",
          term: inputValue,
        });
      }
      inputValue = dt.parse("0").value;
    }
    operations = operations;

    await tick();
    focus();
  }

  async function handleSubtract() {
    parseInput(inputContents);
    await tick();

    if (!initialValue && inputValue?.toNumber(unit) !== 0) {
      initialValue = inputValue;
      inputValue = dt.parse("0").value;
      operations.push({
        operand: "subtract",
        term: null,
      });
    } else if (initialValue && inputValue?.toNumber(unit) === 0) {
      const lastOperation = operations[operations.length - 1];
      if (lastOperation && !lastOperation.term) {
        lastOperation.operand = "subtract";
      } else {
        operations.push({
          operand: "subtract",
          term: null,
        });
      }
    } else if (initialValue && inputValue?.toNumber(unit) !== 0) {
      const lastOperation = operations[operations.length - 1];
      if (lastOperation && !lastOperation.term) {
        lastOperation.operand = "subtract";
        lastOperation.term = inputValue;
      } else {
        operations.push({
          operand: "subtract",
          term: inputValue,
        });
      }
      inputValue = dt.parse("0").value;
    }
    operations = operations;

    await tick();
    focus();
  }

  function clear() {
    initialValue = null;
    inputValue = dt.parse("0").value;
    operations = [];
    focus();
  }

  function cancel() {
    clear();
    dispatch("cancel");
  }

  function insert() {
    if (!result) return;
    dispatch("input", { value: result });
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class="calculator-container" on:click|stopPropagation>
  <div class="calculator">
    <div class="flex gap-1 justify-end items-center flex-wrap text-gray-500 min-h-8">
      {#if initialValue && (!result || operations.length)}
        <div>
          {formatter(initialValue)}
        </div>
      {/if}
      {#each operations as op}
        <div>
          {#if op.operand === "add"}
            +
          {:else if op.operand === "subtract"}
            -
          {/if}
        </div>
        {#if op.term}
          <div>
            {formatter(op.term)}
          </div>
        {/if}
      {/each}
      {#if result}
        {#if operations.length}
          <div>=</div>
        {/if}
        <div class="text-black text-lg">{formatter(result, unit)}</div>
      {/if}
    </div>
    <div class="flex gap-1 justify-end items-center">
      <input bind:this={input} bind:value={inputContents} on:keydown={handleKeydown} on:click={focus} />
    </div>
    <div class="flex gap-2 justify-end items-center">
      <button use:tooltip={{ text: "Clear" }} class="clear-button" on:click={clear}>AC</button>
      <button use:tooltip={{ text: "Subtract" }} class="operation-button" on:click={handleSubtract}>
        <MinusIcon />
      </button>
      <button use:tooltip={{ text: "Add" }} class="operation-button" on:click={handleAdd}>
        <PlusIcon />
      </button>
    </div>
    <div class="flex gap-2 justify-end items-center pt-4">
      <button use:tooltip={{ text: "Cancel" }} class="cancel-button" on:click={cancel}>
        <XIcon />
      </button>
      <button use:tooltip={{ text: "Insert" }} class="check-button" on:click={insert}>
        <CheckIcon />
      </button>
    </div>
  </div>
</div>

<style lang="scss">
  input {
    outline: none;
    min-width: 0;
    max-width: 100%;
    text-align: right;

    @apply p-1 bg-gray-100 border border-gray-200 text-lg w-full;
  }

  .calculator-container {
    @apply absolute top-0 left-0 mt-4 p-4 z-30 w-full;
  }

  .calculator {
    @apply rounded-lg border border-gray-300 shadow-lg bg-white space-y-2 p-4;
  }

  .clear-button {
    @apply rounded p-1 text-sm;

    &:hover,
    &:active {
      @apply bg-gray-200;
    }
  }

  .operation-button {
    @apply rounded p-1 text-sm bg-orange-400 text-white;

    &:hover,
    &:active {
      @apply bg-orange-600;
    }
  }

  .cancel-button {
    @apply rounded p-1 text-sm border border-transparent;

    &:hover,
    &:active {
      @apply border-gray-400;
    }
  }

  .check-button {
    @apply rounded p-1 text-sm border border-transparent;

    &:hover,
    &:active {
      @apply bg-black text-white;
    }
  }
</style>
